import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DatePipe } from '@angular/common';

import { BehaviorSubject, Subscription } from 'rxjs';
import { leftMainMenu } from 'src/app/core/menu/leftMainMenuList';
import { ToastrService } from 'ngx-toastr';
import { ApiConstants } from 'src/app/core/endpointsAndGeneric-constants/api-constants';
import { HttpMethodModifierService } from 'src/app/core/http-method-modifier.service';
import moment from 'moment';
import { JwtHelperService } from '@auth0/angular-jwt';
import { masterConfigurationList } from 'src/app/core/endpointsAndGeneric-constants/masterConfigList';
import { DataService } from './data.service';

@Injectable({
  providedIn: 'root'
})
export class SharedService {
  private title = new BehaviorSubject<string>('');
  getTitle = this.title.asObservable();
  masterConfigurationList: string = '';
  masterRbacList: string[] = [];
  private _istenantAvailable: boolean = false;
  private _blobPath: string = null;
  private _subscription: Subscription;
  private _sessionTimeoutFlag: boolean = false;
  private _toPageNumber :{device: number, template: number, batch : number} = {device: 1, template: 1, batch : 1};

  constructor(
    private _toastr: ToastrService,
    private _router: Router,
    private httpGuard: HttpMethodModifierService,
    public jwtHelper: JwtHelperService,
    private _dataService: DataService
  ) {
    this._subscription = this._dataService?.events?.subscribe((res: any) => {
      if (res.name === 'sessionTimeout') {
        this._sessionTimeoutFlag = true;
      }
    });
  }

  /**
   * Toaster Handler function
   * @param statusDtls 
   */
  toastrHandler(statusDtls: any) {
    if (!this._sessionTimeoutFlag) {
      switch (statusDtls.type) {
      case 'success': this._toastr.success(statusDtls.msg, 'Success', { positionClass: 'toast-top-right', timeOut: 5000 });
        break;
      case 'warning': this._toastr.warning(statusDtls.msg, 'Warning', { positionClass: 'toast-top-right' });
        break;
      case 'error': this._toastr.error(statusDtls.msg, 'Error', { positionClass: 'toast-top-right' });
        break;
      default: this._toastr.warning(statusDtls.msg, 'Warning', { positionClass: 'toast-top-right' });
        break;
      }
    }
  }

  /**
   * Sets title
   * @param title 
   */
  setTitle(title: string) {
    this.title.next(title);
  }

  /**
   * Sets rbac list
   * @param [hierarchy] 
   */
  setRbacList(hierarchy: boolean = false) {
    const params = '?hierarchy=' + hierarchy;
    this.httpGuard.httpReq('get', 'API_ENDPOINT', ApiConstants.URL.features, params).subscribe({
      next: (data: any) => {
        if (data?.length) {
          this.masterRbacList = [...data]?.map((elem: any) => elem.uniqueId);
          localStorage.setItem('rbac', JSON.stringify(this.masterRbacList));
          if (this.getPrivilegeUrl()) {
            this._router.navigateByUrl(this.getPrivilegeUrl());
          }
        } else {
          this.noPrivilegeHanlde();
        }
      },
      error: () => {
        this.noPrivilegeHanlde();
      }
    });
  }

  /**
   * Gets privilege url
   * @returns  
   */
  getPrivilegeUrl() {
    const urlName = [...leftMainMenu.menu].find((item: any) => this.rbacManagement(item.privilege));
    return urlName ? urlName.nav : null;
  }

  /**
   * privilege hanlde
   * @param [toaster] 
   */
  noPrivilegeHanlde(toaster: boolean = true) {
    this.masterRbacList = [];
    localStorage.clear();
    this._router.navigate(['login']);
    if (toaster)
      this.toastrHandler({ type: 'warning', msg: 'Sorry, you dont have relevant privilege to access the portal. Please contact admin and try again.' });
  }

  /**
   * Rbacs management
   * @param [privilege] 
   * @returns  
   */
  rbacManagement(privilege?: any) {
    if (this.masterRbacList?.length) {
      return this.masterRbacList?.includes(privilege);
    } else {
      const rbac = localStorage.getItem('rbac') ? JSON.parse(localStorage.getItem('rbac')) : [];
      if (rbac?.length) {
        this.masterRbacList = rbac;
        return this.masterRbacList?.includes(privilege);
      }
      else {
        this.noPrivilegeHanlde(false);
        return false;
      }
    }
  }

  /**
   * Resets rbac
   */
  resetRbac() {
    this.masterRbacList = [];
  }

  /**
   * privilege navigate
   */
  noPrivilegeNavigate() {
    const urlSplit = this._router.url.split('/');
    if (urlSplit?.length >= 1 && urlSplit[1] === '') {
      for (let i = 0, len = leftMainMenu?.menu?.length; i < len; i++) {
        if (this.rbacManagement(leftMainMenu?.menu?.[i]?.privilege)) {
          this._router.navigate([leftMainMenu?.menu?.[i]?.nav]);
          break;
        }
      }
    }
  }

  /**
   * Numbers only
   * @param event 
   * @param [type] 
   * @returns true if only 
   */
  numberOnly(event: any, type?: string): boolean {
    const charCode = (event.which) ? event.which : event.keyCode;
    if (charCode === 101 || charCode === 69 || charCode === 43 || charCode === 45) {
      return false;
    }
    return true;
  }

  /**
   * Gets timestamp in millisecond
   * @param date 
   * @returns timestamp in millisecond 
   */
  getTimestampInMillisecond(date: Date): number {
    return date ? new Date(date)?.getTime() : 0;
  }

  /**
   * Gets tenant id
   * @returns  
   */
  getTenantId() {
    if(sessionStorage.getItem('deviceModuleTenantId')){
      const tenantId = sessionStorage.getItem('deviceModuleTenantId') || '{}';
      sessionStorage.removeItem('deviceModuleTenantId');
      return tenantId;
    }
    else {
      if (sessionStorage.getItem('tenantId')) {
        return JSON.parse(sessionStorage.getItem('tenantId') || '{}');
      } else {
        return null;
      }
    }
  }

  /**
   * Findgmts offset
   * @param timeZoneName 
   * @returns  
   */
  findgmtOffset(timeZoneName) {
    return 'GMT' + moment.tz(timeZoneName).format('Z')?.toString();
  }

  /**
   * Gets date time str
   * @param date 
   * @returns  
   */
  getDateTimeStr(date: Date) {
    let dateStr =
      ('00' + (date.getMonth() + 1)).slice(-2) + '/' +
      ('00' + date.getDate()).slice(-2) + '/' +
      date.getFullYear() + ' ' +
      ('00' + date.getHours()).slice(-2) + ':' +
      ('00' + date.getMinutes()).slice(-2) + ':' +
      ('00' + date.getSeconds()).slice(-2);

    return dateStr;
  }

  /**
   * Gets file type image
   * @param value 
   * @returns  
   */
  getFileTypeImage(value: string) {
    const fileExtension = this.getFileExtension(value);
    let fileTypeImage: string = 'blob_unsupported.svg';
    switch (fileExtension?.toLowerCase()) {
    case 'doc':
    case 'docx':
    case 'pdf':
    case 'ppt':
    case 'pptx':
    case 'xls':
    case 'xlsx':
      fileTypeImage = 'blob_document.svg';
      break;
    case 'mp4':
    case 'webm':
    case 'avi':
      fileTypeImage = 'blob_video.svg';
      break;
    case 'mp3':
    case 'wav':
      fileTypeImage = 'blob_audio.svg';
      break;
    case 'apng':
    case 'avif':
    case 'gif':
    case 'jpeg':
    case 'jpg':
    case 'png':
    case 'webp':
      fileTypeImage = 'blob_image.svg';
      break;
    }
    return fileTypeImage;
  }

  /**
   * Gets file extension
   * @param value 
   * @returns  
   */
  getFileExtension(value: string) {
    return value ? value.substring(value.lastIndexOf('.') + 1).split('?')[0] : null;
  }

  /**
   * Gets file support status
   * @param fileMimeType 
   * @param supportFileMimeTypes 
   * @returns  
   */
  getFileSupportStatus(fileMimeType: string, supportFileMimeTypes: string[]) {
    let status = false;
    if (fileMimeType && supportFileMimeTypes?.length) {
      status = supportFileMimeTypes.includes(fileMimeType);
    }
    return status;
  }

  /**
   * Blobs upload
   * @param blobDetails 
   * @param blobSasToken 
   * @returns  
   */
  blobUpload(blobDetails: any, blobSasToken: string) {
    const content = blobDetails?.file;
    const url = ApiConstants.URL.blobUpload + '/' + ApiConstants.BLOB_CONTAINER + '/' + this._blobPath + '?' + blobSasToken;
    return this.httpGuard.httpReq('put', 'azureBlobBaseUrl', url, content);
  }

  /**
   * Blobs delete
   * @param blobUrl 
   * @returns  
   */
  blobDelete(blobUrl: any) {
    return this.httpGuard.httpReq('delete', 'azureBlobBaseUrl', blobUrl);
  }

  FILE_MIME_TYPES = [
    // "application/msword",
    // "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    // "application/pdf",
    // "application/vnd.ms-powerpoint",
    // "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    // "application/vnd.ms-excel",
    // "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    // "video/mp4",
    // "video/webm",
    // "video/x-msvideo",
    // "audio/mpeg",
    // "audio/wav",
    // "image/avif",
    // "image/gif",
    // "image/jpeg",
    // "image/png",
    // "image/webp",
    'text/csv'
  ];

  VIDEO_AUDIO_SUPPORTED_TYPE = [
    // "video/mp4",
    // "video/webm",
    // "video/x-msvideo",
    // "audio/mpeg",
    // "audio/wav"
  ];

  THUMBNAIL_MIME_TYPES = [
    // "image/jpeg",
    // "image/png"
  ];

  IMAGE_MIME_TYPES = [
    // "image/jpeg",
    // "image/png",
    // "image/svg+xml"
  ];

  getIconByType(type: string, size: 'small' | 'large') {
    /*
    Unsupported: kb_unsupported.svg | kb_unsupported_large.svg
    Document: kb_document.svg | kb_document_large.svg
    Video: kb_video.svg | kb_video_large.svg
    Audio: kb_audio.svg | kb_audio_large.svg
    Image: kb_image.svg | kb_image_large.svg
    */
    let icon: string = (size === 'small') ? 'kb_unsupported.svg' : 'kb_unsupported_large.svg';
    switch (type.toLowerCase()) {
    case 'doc':
    case 'docx':
    case 'pdf':
    case 'ppt':
    case 'pptx':
    case 'xls':
    case 'xlsx':
      icon = (size === 'small') ? 'kb_document.svg' : 'kb_document_large.svg';
      break;
    case 'mp4':
    case 'webm':
    case 'avi':
      icon = (size === 'small') ? 'kb_video.svg' : 'kb_video_large.svg';
      break;
    case 'mp3':
    case 'wav':
      icon = (size === 'small') ? 'kb_audio.svg' : 'kb_audio_large.svg';
      break;
    case 'apng':
    case 'avif':
    case 'gif':
    case 'jpeg':
    case 'jpg':
    case 'png':
    case 'webp':
      icon = (size === 'small') ? 'kb_image.svg' : 'kb_image_large.svg';
      break;
    }
    return icon;
  }

  /**
   * Gets label by type
   * @param type 
   * @returns  
   */
  getLabelByType(type: string) {
    let label: string = 'Unsupported';

    // Labels: Document, Video, Audio, Image
    switch (type.toLowerCase()) {
    case 'doc':
    case 'docx':
    case 'pdf':
    case 'ppt':
    case 'pptx':
    case 'xls':
    case 'xlsx':
      label = 'Document';
      break;
    case 'mp4':
    case 'webm':
    case 'avi':
      label = 'Video';
      break;
    case 'mp3':
    case 'wav':
      label = 'Audio';
      break;
    case 'apng':
    case 'avif':
    case 'gif':
    case 'jpeg':
    case 'jpg':
    case 'png':
    case 'webp':
      label = 'Image';
      break;
    }
    return label;
  }

  /**
   * Gets file extension from url
   * @param url 
   * @returns  
   */
  getFileExtensionFromUrl(url: string) {
    let extension = '';
    if (url) {
      extension = url.substring(url.lastIndexOf('.') + 1).split('?')[0];
    }
    return extension;
  }

  /**
   * Fetchs token detials
   * @param token 
   * @param [authentication] 
   * @returns true if token detials 
   */
  fetchTokenDetials(token: string, authentication: boolean = false): boolean {
    if (!this.jwtHelper.isTokenExpired(token)) {
      const tokenDtls = this.jwtHelper.decodeToken(token);
      localStorage.setItem('token', token);
      sessionStorage.setItem('userId', tokenDtls?.user?.userId);
      sessionStorage.setItem('username', tokenDtls?.user?.userName);
      sessionStorage.setItem('source', tokenDtls?.user?.source);
      if (tokenDtls?.user?.tenantId) {
        sessionStorage.setItem('tenantId', JSON.stringify(tokenDtls?.user?.tenantId));
        this._istenantAvailable = false;
      } else {
        this._istenantAvailable = true;
        this.setTenantAvailability();
      }
      if (!authentication) {
        this.getAttributeDetails(masterConfigurationList);
      } else {
        this.setRbacList();
      }
    }
    return !this.jwtHelper.isTokenExpired(token);
  }

  /**
   * Sets tenant availability
   */
  setTenantAvailability() {
    if (this._istenantAvailable) {
      this.getTenants().subscribe({
        next: (result: any) => {
          if (result?.status) {
            const tenantList = [{
              name: 'All Tenants',
              _id: null
            }, ...result['response']['data']];
            localStorage.setItem('tenantList', JSON.stringify(tenantList));
            this._dataService.broadcast('tenantsListStored');
          }
        },
        error: (result) => {
          this.toastrHandler({
            type: 'error',
            msg: result?.error,
          });
        },
      });
    }
  }

  /**
   * Gets tenant availability
   * @returns  
   */
  getTenantAvailability() {
    return this._istenantAvailable;
  }

  /**
   * Gets tenants
   * @returns  
   */
  getTenants() {
    return this.httpGuard.httpReq('get', 'API_ENDPOINT', ApiConstants.URL.getTenants);
  }

  /**
   * Generates sas token
   * @param blobDetails 
   * @returns  
   */
  generateSasToken(blobDetails: any) {
    const dateTime = new DatePipe('en-US').transform(Date.now(), 'YYYYMMddHHmmssSSSS');
    this._blobPath = blobDetails?.baseUrl + '/' + dateTime + '_' + blobDetails?.file?.name.trim().replaceAll(' ', '_');
    const params = '?name=' + this._blobPath;
    return this.httpGuard.httpReq('get-text-type', 'API_ENDPOINT', ApiConstants.URL.generateSasToken, params);
  }

  /**
   * Gets attribute details
   * @param attributes 
   * @param [isLoaderNeeded] 
   */
  getAttributeDetails(attributes: any, isLoaderNeeded: boolean = true) {
    const postData = { attributes: attributes };
    let mappedConfigList = [];
    this.httpGuard.httpReq('post', 'API_ENDPOINT', ApiConstants.URL.attributeDetails, postData, isLoaderNeeded).subscribe(
      {
        next: (data: any) => {
          if (data.length) {
            mappedConfigList = data.map((item: any) => {
              const obj = {};
              obj['attribute'] = item.attribute;
              obj['values'] = item.values;
              return obj;
            });
            this.setAttributes(mappedConfigList);
          } else {
            this.setAttributes(mappedConfigList);
          }
          if (!mappedConfigList.length) {
            // this._sharedService.toastrHandler({ type: 'warning', msg: 'Configuration list is not available, please provide the required configuration for the app' });
          }
          this._dataService.broadcast('attributeDetailsUpdated');
        }, error: () => {
          this._dataService.broadcast('attributeDetailsUpdated');
        }
      });
  }

  /**
   * Sets attributes
   * @param attributesList 
   */
  setAttributes(attributesList: any) {
    this.masterConfigurationList = JSON.stringify(attributesList);
  }

  /**
   * Gets attributes
   * @param attributeName 
   * @param [returnValue] 
   * @returns  
   */
  getAttributes(attributeName: string, returnValue: string = 'values') {
    if (this.masterConfigurationList && this.masterConfigurationList.length) {
      const configuration = JSON.parse(this.masterConfigurationList).find((elem: any) => elem.attribute === attributeName);
      if (configuration) {
        return returnValue === 'values' ? configuration.values : configuration;
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  /**
   * Gets last seen format
   * @param sourceTimestamp 
   * @returns  
   */
  getLastSeenFormat(sourceTimestamp: any) {
    if (!sourceTimestamp)
      return null;
    const dateDiff = Math.floor((new Date().valueOf() - new Date(sourceTimestamp).valueOf()) / (1000 * 60 * 60 * 24));
    if (!dateDiff) {
      const differ = new Date().getTime() - new Date(sourceTimestamp).getTime();
      const days = Math.floor(differ / (60 * 60 * 24 * 1000));
      const hours = Math.floor(differ / (60 * 60 * 1000)) - (days * 24);
      const minutes = Math.floor(differ / (60 * 1000)) - ((days * 24 * 60) + (hours * 60));
      const seconds = Math.floor(differ / 1000) - ((days * 24 * 60 * 60) + (hours * 60 * 60) + (minutes * 60));
      return (hours ? hours + ' Hours, ' : '').toString() + (minutes ? (minutes === 1 ? minutes + ' ,Min,' : minutes + ' ,Mins,') : '').toString() + ((!minutes && !hours) ? ' Just Now' : ' Ago').toString();
    } else {
      return '' + new DatePipe('en-US').transform(sourceTimestamp, 'mediumDate');
      // return ''+ dateDiff + ' Days ago';
    }
  }
  /**
   * Saves page number
   * @param pageNumber 
   * @param pageType 
   */
  savePageNumber(pageNumber: number, pageType: string) {
    this._toPageNumber[pageType] = pageNumber;
  }

  /**
   * Gets page number
   * @param pageType 
   * @returns  
   */
  getPageNumber(pageType: string) {
    return this._toPageNumber?.[pageType] ?? 1;
  }
}
